// Exotics Pricer page — band-row composition matching MVP App.jsx.
// Each band is one or more BandRows (label-on-left, N cells across).
// Trade splits into a banner + 8 sub-rows per MVP.
//
// Colours mirror the exact values Diletta's production widget chrome
// uses (src/components/v2/widget/styles.scss):
//   - Container border: #026aa2 (cyan-blue)
//   - Container bg: rgba(40, 114, 226, 0.10) semi-transparent blue
//   - Box-shadow + backdrop-filter blur per the widget pattern
//   - Inner dividers: #416077 (mid grey-cyan)
//   - Banner bg: #1f3645 (highlightLevel1)
//   - Title text: #c2e5ff (light cyan-white)
import { useContext, useEffect } from 'react';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

import ExoticsCalculateBand from '../../components/v2/widgets/exotics-calculate-band';
import ExoticsMarketBand from '../../components/v2/widgets/exotics-market-band';
import ExoticsRiskBand from '../../components/v2/widgets/exotics-risk-band';
import ExoticsSabrBand from '../../components/v2/widgets/exotics-sabr-band';
import ExoticsModelBand from '../../components/v2/widgets/exotics-model-band';
import ExoticsRiskLadderModal from '../../components/v2/widgets/exotics-risk-ladder-modal';
import {
  BandRow,
  InstrumentCell,
  ContractCell,
  ExpiryCell,
  NotionalCell,
  StrikeCell,
  UpperBarrierCell,
  LowerBarrierCell,
  PayTypeCell,
  CallPutCell,
  StatusCell,
  dilettaPanelSx,
  DILETTA_BANNER_BG,
  DILETTA_TITLE,
} from '../../components/v2/widgets/exotics-shared';
import { CONTRACT_SCHEMA } from './contracts';
import { PricingContext, PricingProvider } from '../../providers/exotics-pricing-provider';
import {
  ExoticsTabsContext,
  ExoticsTabsProvider,
  lockedTokenFromTabKey,
  isMpHandoffKey,
} from '../../providers/exotics-tabs/exotics-tabs-provider';
import { useAvailableTokens } from '../../providers/exotics-tokens/use-available-tokens';
import ExoticsTabsBar from './exotics-tabs-bar';
import LockedTokenHeader from './locked-token-header';
import { HandoffTabOrchestrator, HandoffSeeder } from './handoff-bridge';
import { DEFAULT_INSTRUMENT_COUNT, MAX_INSTRUMENTS } from './constants';

import './styles.scss';

// Body of a single tab — the existing pricing page. Pulled out from the
// top-level export so each tab can wrap it in its own per-tab
// PricingProvider (with its own instrument list and an optional locked
// token from the tab key). The widgets inside read the nearest
// PricingContext, which resolves to the inner per-tab provider.
const PricingTabContent = () => {
  const {
    instruments,
    addInstrument,
    removeInstrument,
    duplicateInstrument,
    calculateAll,
    lockedToken,
  } = useContext(PricingContext);
  const { tokens: availableTokens } = useAvailableTokens();
  // Look up the locked token's display info from the registry so the page
  // title and pair labels reflect the right base/quote currencies for any
  // pair (BTC · USD, SOL · USDC, TRUMP · USDT, etc.) — not always USDC.
  const lockedTokenInfo = lockedToken
    ? availableTokens.find((t) => t.baseQuote === lockedToken)
    : undefined;
  const lockedPairLabel = lockedToken
    ? `${lockedTokenInfo?.baseCcy ?? lockedToken} · ${lockedTokenInfo?.quoteCcy ?? 'USDC'}`
    : '';
  useEffect(() => {
    if (instruments.length === 0) {
      for (let i = 0; i < DEFAULT_INSTRUMENT_COUNT; i++) {
        addInstrument();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const atCapacity = instruments.length >= MAX_INSTRUMENTS;
  const n = instruments.length;

  // Conditional Trade sub-rows only render if AT LEAST ONE instrument's
  // contract declares the field. Otherwise the whole row (label + N
  // cells) is dropped — keeps the Trade block compact when none of the
  // active contracts care about strike / barriers / pay-type.
  const anyStrike = instruments.some((i) => CONTRACT_SCHEMA[i.contract].strike);
  const anyUpper = instruments.some((i) => CONTRACT_SCHEMA[i.contract].upper);
  const anyLower = instruments.some((i) => CONTRACT_SCHEMA[i.contract].lower);
  const anyCallPut = instruments.some((i) => CONTRACT_SCHEMA[i.contract].callPut);
  const anyPayType = instruments.some((i) => CONTRACT_SCHEMA[i.contract].payType);
  // The LAST visible Trade row carries the divider that closes the Trade
  // block. As conditional rows drop out, the divider moves up. Order matches
  // the render order below: notional → strike → callPut → upper → lower → payType.
  const lastTradeRow: 'payType' | 'lower' | 'upper' | 'callPut' | 'strike' | 'notional' = anyPayType
    ? 'payType'
    : anyLower
      ? 'lower'
      : anyUpper
        ? 'upper'
        : anyCallPut
          ? 'callPut'
          : anyStrike
            ? 'strike'
            : 'notional';

  // width: 100% so the inner cells span their full grid column (otherwise
  // a flex parent would shrink the Stack to its content width and the row
  // would cluster on the left).
  const renderCells = (render: (id: string, columnIndex: number) => React.ReactNode) =>
    instruments.map((inst, columnIndex) => (
      <Box key={inst.id} sx={{ minWidth: 0, width: '100%' }}>
        {render(inst.id, columnIndex)}
      </Box>
    ));

  return (
    <Box className="pricing-container" sx={{ pt: 2, px: 3, pb: 3 }}>
      {/* Toolbar — symmetric vertical breathing room top + bottom so
          the gap below the toolbar matches the gap above (between the
          dashboard tab strip and the Add Instrument button). */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography
            sx={{ color: DILETTA_TITLE, fontWeight: 600, fontSize: 16, letterSpacing: '0' }}
          >
            {lockedToken ? `${lockedPairLabel} ` : ''}Exotics Pricer
          </Typography>
        </Stack>
        <Box sx={{ flexGrow: 1 }} />

        <Tooltip
          title={
            atCapacity ? 'Maximum twenty — duplicate or remove to add another' : 'Add instrument'
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: 10 }} />}
              onClick={() => addInstrument()}
              disabled={atCapacity}
              sx={{ fontSize: 11, textTransform: 'none', py: 0.5, px: 1.5, minWidth: 0 }}
            >
              Add Instrument
            </Button>
          </span>
        </Tooltip>
        <Button
          size="small"
          variant="contained"
          onClick={calculateAll}
          disabled={n === 0}
          sx={{ fontSize: 11, textTransform: 'none', py: 0.5, px: 1.75, minWidth: 0 }}
        >
          Calculate All
        </Button>
      </Stack>

      {/* Locked-token header — renders only on single-token tabs
          (returns null when lockedToken is null). Shows pair + live spot
          price from the Deribit ticker channel. */}
      <LockedTokenHeader />

      {/* Band-row stack. Trade banner + 8 sub-rows, then Calculate /
          Last Calc / Market / Risk / Surface Fit / Model.
          Fixed-width columns per MVP App.jsx — the outer Box scrolls
          horizontally if the column count outgrows the viewport;
          width:fit-content + minWidth:100% on the inner container means
          the container fills the viewport when it can and scrolls when
          it can't. */}
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ ...dilettaPanelSx, overflow: 'hidden', width: 'fit-content', minWidth: '100%' }}>
          {/* TRADE banner — Diletta highlightLevel1 bg + cyan-white title */}
          <Box
            sx={{
              backgroundColor: DILETTA_BANNER_BG,
              px: 1.5,
              py: 0.75,
            }}
          >
            <Typography
              sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', color: DILETTA_TITLE }}
            >
              TRADE
            </Typography>
          </Box>

          <BandRow label="Instrument" n={n} divider={false}>
            {renderCells((id, columnIndex) => (
              <InstrumentCell
                instrumentId={id}
                columnIndex={columnIndex}
                atCapacity={atCapacity}
                onDuplicate={duplicateInstrument}
                onRemove={removeInstrument}
              />
            ))}
          </BandRow>
          {/* Inner Trade sub-rows: no dividers between them. Only the LAST
            sub-row (Pay Type) carries the divider that closes the Trade
            block — matches the MVP TradeBand layout. */}
          <BandRow label="Contract" n={n} divider={false}>
            {renderCells((id, columnIndex) => (
              <ContractCell instrumentId={id} columnIndex={columnIndex} />
            ))}
          </BandRow>
          <BandRow label="Expiry" n={n} divider={false}>
            {renderCells((id, columnIndex) => (
              <ExpiryCell instrumentId={id} columnIndex={columnIndex} />
            ))}
          </BandRow>
          <BandRow label="Notional" n={n} divider={lastTradeRow === 'notional'}>
            {renderCells((id, columnIndex) => (
              <NotionalCell instrumentId={id} columnIndex={columnIndex} />
            ))}
          </BandRow>
          {anyStrike && (
            <BandRow label="Strike" n={n} divider={lastTradeRow === 'strike'}>
              {renderCells((id, columnIndex) => (
                <StrikeCell instrumentId={id} columnIndex={columnIndex} />
              ))}
            </BandRow>
          )}
          {anyCallPut && (
            <BandRow label="Call / Put" n={n} divider={lastTradeRow === 'callPut'}>
              {renderCells((id, columnIndex) => (
                <CallPutCell instrumentId={id} columnIndex={columnIndex} />
              ))}
            </BandRow>
          )}
          {anyUpper && (
            <BandRow label="Upper Barrier" n={n} divider={lastTradeRow === 'upper'}>
              {renderCells((id, columnIndex) => (
                <UpperBarrierCell instrumentId={id} columnIndex={columnIndex} />
              ))}
            </BandRow>
          )}
          {anyLower && (
            <BandRow label="Lower Barrier" n={n} divider={lastTradeRow === 'lower'}>
              {renderCells((id, columnIndex) => (
                <LowerBarrierCell instrumentId={id} columnIndex={columnIndex} />
              ))}
            </BandRow>
          )}
          {anyPayType && (
            <BandRow label="Pay Type" n={n}>
              {renderCells((id, columnIndex) => (
                <PayTypeCell instrumentId={id} columnIndex={columnIndex} />
              ))}
            </BandRow>
          )}

          {/* Also drop the divider on the Instrument row above. */}
          {/* Calculate row: drop cellShell so the button sits directly on
            the container bg (slightly darker than the cell-shell tint). */}
          <BandRow label="Calculate" n={n} divider={false} cellShell={false}>
            {renderCells((id) => (
              <ExoticsCalculateBand instrumentId={id} />
            ))}
          </BandRow>
          <BandRow label="Last Calc" n={n}>
            {renderCells((id) => (
              <StatusCell instrumentId={id} />
            ))}
          </BandRow>

          {/* Cells with their own card chrome (Market, Risk, SABR, Model)
            opt out of the BandRow cellShell to avoid double chrome. */}
          <BandRow label="Market Snapshot" n={n} cellShell={false}>
            {renderCells((id) => (
              <ExoticsMarketBand instrumentId={id} />
            ))}
          </BandRow>
          <BandRow label="Pricing & Risk" n={n} cellShell={false}>
            {renderCells((id) => (
              <ExoticsRiskBand instrumentId={id} />
            ))}
          </BandRow>
          <BandRow label="Surface Fit" n={n} cellShell={false}>
            {renderCells((id) => (
              <ExoticsSabrBand instrumentId={id} />
            ))}
          </BandRow>
          <BandRow label="Model" n={n} cellShell={false} divider={false}>
            {renderCells((id) => (
              <ExoticsModelBand instrumentId={id} />
            ))}
          </BandRow>
        </Box>
      </Box>

      <ExoticsRiskLadderModal />
    </Box>
  );
};

// Per-tab layer: mounts one PricingProvider per tab, each with its own
// in-memory instrument list. Hidden tabs stay mounted (display:none) so
// their state survives a tab switch — matches the OTC trader pattern.
const PricingTabsLayout = () => {
  const { tabs, currentTab } = useContext(ExoticsTabsContext);
  return (
    // pt:8 (64px) clears Diletta's fixed AppBar — without this the tab
    // strip renders at viewport top:0 and is hidden behind the AppBar.
    // The pricing-container inside PricingTabContent has its own pt:4
    // dropped to 0 below so the page doesn't end up double-padded.
    <Box sx={{ pt: 8 }}>
      <ExoticsTabsBar />
      {/* Watches the MP handoff intent and ensures/selects the stable handoff
          tab. Lives at the tab layer so it can create the tab the seeder needs. */}
      <HandoffTabOrchestrator />
      {tabs.map((tab) => {
        // Metadata lock (per-token MP handoff tabs → base_quote) takes
        // precedence over the key-derived lock (ordinary custom tabs).
        const lockedToken = tab.lockedToken ?? lockedTokenFromTabKey(tab.key);
        const isCurrent = tab.key === currentTab;
        const isHandoffTab = isMpHandoffKey(tab.key);
        return (
          <Box
            key={tab.key}
            sx={{ display: isCurrent ? 'block' : 'none' }}
            role="tabpanel"
            aria-hidden={!isCurrent}
          >
            <PricingProvider lockedToken={lockedToken ?? undefined}>
              <PricingTabContent />
              {/* Rendered AFTER PricingTabContent so its seed effect runs after
                  the default-instrument effect — the wholesale replace wins. */}
              {isHandoffTab && lockedToken && <HandoffSeeder token={lockedToken} />}
            </PricingProvider>
          </Box>
        );
      })}
    </Box>
  );
};

// Top-level export: wraps the page in ExoticsTabsProvider so the tab
// state (list, current, add/rename/duplicate/delete) is available to
// every tab control and panel below.
// Renamed from `Pricing` to `ExoticsPricer` (IM-1580) to disambiguate from the
// legacy `pages/pricing` `Pricing` component, which remains live and untouched.
// This is the only public name change applied during the replay.
const ExoticsPricer = () => (
  <ExoticsTabsProvider>
    <PricingTabsLayout />
  </ExoticsTabsProvider>
);

export default ExoticsPricer;
